import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, CreditCard, Wallet, Wrench, Settings, LogOut, Shield, Sword, Crown, ArrowDown, Check, Lock, Mail, AlertTriangle, ChevronRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { BackgroundMusic } from '../components/BackgroundMusic';
import { useAuth } from '../contexts/AuthContext';
type Language = 'es' | 'en' | 'pt';
type Tab = 'overview' | 'buy' | 'withdraw' | 'unstuck' | 'settings';
const translations = {
  es: {
    login: {
      title: 'Iniciar Sesión',
      subtitle: 'Accede a tu cuenta de Vanguard',
      username: 'Usuario',
      password: 'Contraseña',
      button: 'Entrar al Portal',
      noAccount: '¿No tienes cuenta?',
      register: 'Regístrate aquí'
    },
    sidebar: {
      overview: 'Resumen de Cuenta',
      buy: 'Comprar V Coins',
      withdraw: 'Retirar a USDT',
      unstuck: 'Destrabar Personaje',
      settings: 'Ajustes',
      logout: 'Cerrar Sesión'
    },
    overview: {
      title: 'Resumen de Cuenta',
      welcome: 'Bienvenido de vuelta',
      balance: 'Balance de V Coins',
      status: 'Estado de Cuenta',
      vip: 'VIP Activo',
      standard: 'Estándar',
      characters: 'Tus Personajes',
      level: 'Nivel',
      class: 'Clase',
      server: 'Servidor',
      stats: 'Estadísticas',
      playTime: 'Tiempo de Juego',
      kills: 'Asesinatos',
      deaths: 'Muertes'
    },
    buy: {
      title: 'Comprar V Coins',
      subtitle: 'Selecciona un paquete para potenciar tu experiencia',
      packages: 'Paquetes Disponibles',
      payment: 'Método de Pago',
      purchase: 'Comprar Ahora',
      selected: 'Seleccionado',
      select: 'Seleccionar',
      processing: 'Procesando...',
      success: '¡Compra exitosa! V Coins añadidos a tu cuenta.'
    },
    withdraw: {
      title: 'Retirar V Coin a USDT',
      balance: 'Balance de V Coin',
      amount: 'Cantidad a Retirar',
      receive: 'Recibirás',
      confirmText: 'Confirmar retiro de {vcoin} V Coin por {usdt} USDT',
      button: 'CONFIRMAR RETIRO',
      processing: 'Procesando...',
      success: '¡Retiro solicitado con éxito! Se procesará en 24-48 horas.',
      error: 'Fondos insuficientes o cantidad inválida.'
    },
    unstuck: {
      title: 'Destrabar Personaje',
      subtitle: 'Mueve tu personaje a la zona segura de Lorencia',
      selectChar: 'Seleccionar Personaje',
      location: 'Ubicación Actual',
      warning: 'Tu personaje será movido a las coordenadas seguras de Lorencia (125, 125).',
      cooldown: 'Tiempo de espera: Puedes usar esto una vez cada 5 minutos.',
      button: 'Destrabar Personaje',
      processing: 'Moviendo...',
      success: '¡Personaje movido a Lorencia con éxito!'
    },
    settings: {
      title: 'Ajustes de Cuenta',
      info: 'Información de la Cuenta',
      email: 'Correo Electrónico',
      regDate: 'Fecha de Registro',
      changePass: 'Cambiar Contraseña',
      currentPass: 'Contraseña Actual',
      newPass: 'Nueva Contraseña',
      confirmPass: 'Confirmar Contraseña',
      updateBtn: 'Actualizar Contraseña',
      success: '¡Contraseña actualizada con éxito!'
    }
  },
  en: {
    login: {
      title: 'Login',
      subtitle: 'Access your Vanguard account',
      username: 'Username',
      password: 'Password',
      button: 'Enter Portal',
      noAccount: "Don't have an account?",
      register: 'Register here'
    },
    sidebar: {
      overview: 'Account Overview',
      buy: 'Buy V Coins',
      withdraw: 'Withdraw to USDT',
      unstuck: 'Unstuck Character',
      settings: 'Settings',
      logout: 'Logout'
    },
    overview: {
      title: 'Account Overview',
      welcome: 'Welcome back',
      balance: 'V Coin Balance',
      status: 'Account Status',
      vip: 'VIP Active',
      standard: 'Standard',
      characters: 'Your Characters',
      level: 'Level',
      class: 'Class',
      server: 'Server',
      stats: 'Statistics',
      playTime: 'Play Time',
      kills: 'Kills',
      deaths: 'Deaths'
    },
    buy: {
      title: 'Buy V Coins',
      subtitle: 'Select a package to power up your experience',
      packages: 'Available Packages',
      payment: 'Payment Method',
      purchase: 'Purchase Now',
      selected: 'Selected',
      select: 'Select',
      processing: 'Processing...',
      success: 'Purchase successful! V Coins added to your account.'
    },
    withdraw: {
      title: 'Withdraw V Coin to USDT',
      balance: 'V Coin Balance',
      amount: 'Amount to Withdraw',
      receive: 'You Receive',
      confirmText: 'Confirm withdrawal of {vcoin} V Coin for {usdt} USDT',
      button: 'CONFIRM WITHDRAWAL',
      processing: 'Processing...',
      success: 'Withdrawal requested successfully! It will be processed in 24-48 hours.',
      error: 'Insufficient funds or invalid amount.'
    },
    unstuck: {
      title: 'Unstuck Character',
      subtitle: 'Move your character to the Lorencia safe zone',
      selectChar: 'Select Character',
      location: 'Current Location',
      warning: 'Your character will be moved to the safe coordinates of Lorencia (125, 125).',
      cooldown: 'Cooldown: You can use this once every 5 minutes.',
      button: 'Unstuck Character',
      processing: 'Moving...',
      success: 'Character moved to Lorencia successfully!'
    },
    settings: {
      title: 'Account Settings',
      info: 'Account Information',
      email: 'Email Address',
      regDate: 'Registration Date',
      changePass: 'Change Password',
      currentPass: 'Current Password',
      newPass: 'New Password',
      confirmPass: 'Confirm Password',
      updateBtn: 'Update Password',
      success: 'Password updated successfully!'
    }
  },
  pt: {
    login: {
      title: 'Entrar',
      subtitle: 'Acesse sua conta Vanguard',
      username: 'Usuário',
      password: 'Senha',
      button: 'Entrar no Portal',
      noAccount: 'Não tem uma conta?',
      register: 'Registre-se aqui'
    },
    sidebar: {
      overview: 'Resumo da Conta',
      buy: 'Comprar V Coins',
      withdraw: 'Sacar para USDT',
      unstuck: 'Destravar Personagem',
      settings: 'Configurações',
      logout: 'Sair'
    },
    overview: {
      title: 'Resumo da Conta',
      welcome: 'Bem-vindo de volta',
      balance: 'Saldo de V Coins',
      status: 'Status da Conta',
      vip: 'VIP Ativo',
      standard: 'Padrão',
      characters: 'Seus Personagens',
      level: 'Nível',
      class: 'Classe',
      server: 'Servidor',
      stats: 'Estatísticas',
      playTime: 'Tempo de Jogo',
      kills: 'Abates',
      deaths: 'Mortes'
    },
    buy: {
      title: 'Comprar V Coins',
      subtitle: 'Selecione um pacote para potencializar sua experiência',
      packages: 'Pacotes Disponíveis',
      payment: 'Método de Pagamento',
      purchase: 'Comprar Agora',
      selected: 'Selecionado',
      select: 'Selecionar',
      processing: 'Processando...',
      success: 'Compra bem-sucedida! V Coins adicionados à sua conta.'
    },
    withdraw: {
      title: 'Sacar V Coin para USDT',
      balance: 'Saldo de V Coin',
      amount: 'Quantidade a Sacar',
      receive: 'Você Recebe',
      confirmText: 'Confirmar saque de {vcoin} V Coin por {usdt} USDT',
      button: 'CONFIRMAR SAQUE',
      processing: 'Processando...',
      success: 'Saque solicitado com sucesso! Será processado em 24-48 horas.',
      error: 'Fundos insuficientes ou quantidade inválida.'
    },
    unstuck: {
      title: 'Destravar Personagem',
      subtitle: 'Mova seu personagem para a zona segura de Lorencia',
      selectChar: 'Selecionar Personagem',
      location: 'Localização Atual',
      warning: 'Seu personagem será movido para as coordenadas seguras de Lorencia (125, 125).',
      cooldown: 'Tempo de recarga: Você pode usar isso uma vez a cada 5 minutos.',
      button: 'Destravar Personagem',
      processing: 'Movendo...',
      success: 'Personagem movido para Lorencia com sucesso!'
    },
    settings: {
      title: 'Configurações da Conta',
      info: 'Informações da Conta',
      email: 'Endereço de E-mail',
      regDate: 'Data de Registro',
      changePass: 'Mudar Senha',
      currentPass: 'Senha Atual',
      newPass: 'Nova Senha',
      confirmPass: 'Confirmar Senha',
      updateBtn: 'Atualizar Senha',
      success: 'Senha atualizada com sucesso!'
    }
  }
};
// Mock Data
const mockUser = {
  username: 'DarkLord99',
  email: 'darklord@example.com',
  vCoins: 32500,
  isVip: true,
  regDate: '2025-01-15',
  playTime: '450h 23m',
  kills: 12450,
  deaths: 342,
  characters: [{
    id: 1,
    name: 'ShadowBlade',
    class: 'Blade Knight',
    level: 400,
    resets: 50,
    server: 'Vanguard-1',
    location: 'Icarus (45, 210)'
  }, {
    id: 2,
    name: 'MysticSoul',
    class: 'Soul Master',
    level: 350,
    resets: 25,
    server: 'Vanguard-1',
    location: 'Tarkan (120, 150)'
  }]
};
const vCoinPackages = [{
  id: 'pkg1',
  amount: 100,
  price: 10,
  bonus: 0
}, {
  id: 'pkg2',
  amount: 500,
  price: 45,
  bonus: 50
}, {
  id: 'pkg3',
  amount: 1000,
  price: 80,
  bonus: 150
}, {
  id: 'pkg4',
  amount: 5000,
  price: 350,
  bonus: 1000
}];
export function DashboardPage() {
  const {
    isLoggedIn,
    login,
    logout
  } = useAuth();
  const [language, setLanguage] = useState<Language>('es');
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  // Login State
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  // Withdraw State
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [withdrawStatus, setWithdrawStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  // Buy State
  const [selectedPackage, setSelectedPackage] = useState<string>('pkg2');
  const [buyStatus, setBuyStatus] = useState<'idle' | 'processing' | 'success'>('idle');
  // Unstuck State
  const [selectedChar, setSelectedChar] = useState<number>(mockUser.characters[0].id);
  const [unstuckStatus, setUnstuckStatus] = useState<'idle' | 'processing' | 'success'>('idle');
  // Settings State
  const [settingsStatus, setSettingsStatus] = useState<'idle' | 'success'>('idle');
  useEffect(() => {
    window.scrollTo(0, 0);
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang && ['es', 'en', 'pt'].includes(savedLang)) {
      setLanguage(savedLang);
    }
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  const t = translations[language];
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginUser && loginPass) {
      login(loginUser);
    }
  };
  const handleWithdraw = () => {
    const amount = parseInt(withdrawAmount);
    if (isNaN(amount) || amount <= 0 || amount > mockUser.vCoins) {
      setWithdrawStatus('error');
      setTimeout(() => setWithdrawStatus('idle'), 3000);
      return;
    }
    setWithdrawStatus('processing');
    setTimeout(() => {
      setWithdrawStatus('success');
      setWithdrawAmount('');
      setTimeout(() => setWithdrawStatus('idle'), 3000);
    }, 1500);
  };
  const handleBuy = () => {
    setBuyStatus('processing');
    setTimeout(() => {
      setBuyStatus('success');
      setTimeout(() => setBuyStatus('idle'), 3000);
    }, 1500);
  };
  const handleUnstuck = () => {
    setUnstuckStatus('processing');
    setTimeout(() => {
      setUnstuckStatus('success');
      setTimeout(() => setUnstuckStatus('idle'), 3000);
    }, 1500);
  };
  const handleSettings = (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsStatus('success');
    setTimeout(() => setSettingsStatus('idle'), 3000);
  };
  // Calculate USDT receive amount (100 V Coin = 1 USDT)
  const usdtReceive = withdrawAmount ? (parseInt(withdrawAmount) / 100).toFixed(2) : '0.00';
  if (!isLoggedIn) {
    return <div className="min-h-screen bg-darkBlue text-white font-sans selection:bg-gold/30 selection:text-white flex flex-col" data-id="element-480">
        <BackgroundMusic data-id="element-481" />
        <Navigation data-id="element-482" />

        <main className="flex-grow relative flex items-center justify-center pt-48 pb-40 lg:pb-52 px-4 min-h-screen" data-id="element-483">
          {/* Warriors Background */}
          <div className="absolute inset-0 z-0" data-id="element-484">
            <img src="https://cdn.magicpatterns.com/uploads/xfAj77taGj8Z7M4Qc8mYyx/ChatGPT_Image_Mar_27,_2026,_12_37_09_PM.png" alt="Vanguard Warriors" className="w-full h-full object-cover object-center" data-id="element-485" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-darkBlue/40" data-id="element-486" />
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="glass-panel rounded-2xl p-8 md:p-12 w-full max-w-md relative z-10 border-gold/20 shadow-[0_0_50px_rgba(212,175,55,0.1)]" data-id="element-487">
            <div className="text-center mb-8" data-id="element-488">
              <div className="w-16 h-16 mx-auto bg-darkerBlue rounded-full border border-gold/30 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(212,175,55,0.2)]" data-id="element-489">
                <User className="w-8 h-8 text-gold" data-id="element-490" />
              </div>
              <h1 className="text-3xl font-bold font-cinzel mb-2" data-id="element-491">
                {t.login.title}
              </h1>
              <p className="text-gray-400" data-id="element-492">{t.login.subtitle}</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6" data-id="element-493">
              <div data-id="element-494">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-495">
                  {t.login.username}
                </label>
                <div className="relative" data-id="element-496">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-497">
                    <User className="h-5 w-5 text-gray-500" data-id="element-498" />
                  </div>
                  <input type="text" required value={loginUser} onChange={e => setLoginUser(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-white/10 rounded-lg bg-darkerBlue/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-transparent transition-all" placeholder="DarkLord99" data-id="element-499" />
                </div>
              </div>

              <div data-id="element-500">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-501">
                  {t.login.password}
                </label>
                <div className="relative" data-id="element-502">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-503">
                    <Lock className="h-5 w-5 text-gray-500" data-id="element-504" />
                  </div>
                  <input type="password" required value={loginPass} onChange={e => setLoginPass(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-white/10 rounded-lg bg-darkerBlue/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-transparent transition-all" placeholder="••••••••" data-id="element-505" />
                </div>
              </div>

              <button type="submit" className="w-full btn-primary py-3 text-lg mt-4" data-id="element-506">
                {t.login.button}
              </button>
            </form>

            <div className="mt-8 text-center border-t border-white/10 pt-6" data-id="element-507">
              <p className="text-gray-400 text-sm" data-id="element-508">
                {t.login.noAccount}{' '}
                <a href="/#register" className="text-gold hover:text-yellow-400 font-semibold transition-colors" data-id="element-509">
                  {t.login.register}
                </a>
              </p>
            </div>
          </motion.div>
        </main>
        <Footer data-id="element-510" />
      </div>;
  }
  // LOGGED IN DASHBOARD
  const tabs = [{
    id: 'overview',
    icon: User,
    label: t.sidebar.overview
  }, {
    id: 'buy',
    icon: CreditCard,
    label: t.sidebar.buy
  }, {
    id: 'withdraw',
    icon: Wallet,
    label: t.sidebar.withdraw
  }, {
    id: 'unstuck',
    icon: Wrench,
    label: t.sidebar.unstuck
  }, {
    id: 'settings',
    icon: Settings,
    label: t.sidebar.settings
  }] as const;
  return <div className="min-h-screen bg-darkBlue text-white font-sans selection:bg-gold/30 selection:text-white flex flex-col" data-id="element-511">
      <BackgroundMusic data-id="element-512" />
      <Navigation data-id="element-513" />

      <main className="flex-grow pt-32 pb-24 relative" data-id="element-514">
        {/* Fantasy Landscape Background */}
        <div className="absolute inset-0 z-0" data-id="element-515">
          <img src="https://cdn.magicpatterns.com/uploads/aj6WysGqM5MqYSqQzX8AWP/ChatGPT_Image_Mar_25,_2026,_10_00_56_AM.png" alt="Fantasy Landscape" className="w-full h-full object-cover" data-id="element-516" />
          <div className="absolute inset-0 bg-darkBlue/70" data-id="element-517" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-518">
          <div className="flex flex-col md:flex-row gap-8" data-id="element-519">
            {/* Sidebar (Desktop) / Tabs (Mobile) */}
            <div className="w-full md:w-64 flex-shrink-0" data-id="element-520">
              <div className="glass-panel rounded-xl p-4 sticky top-32" data-id="element-521">
                {/* User Info Mini */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10 px-2" data-id="element-522">
                  <div className="w-12 h-12 rounded-full bg-darkerBlue border border-gold/30 flex items-center justify-center flex-shrink-0" data-id="element-523">
                    <User className="w-6 h-6 text-gold" data-id="element-524" />
                  </div>
                  <div className="overflow-hidden" data-id="element-525">
                    <h3 className="font-bold text-lg truncate" data-id="element-526">
                      {mockUser.username}
                    </h3>
                    <p className="text-xs text-gold flex items-center gap-1" data-id="element-527">
                      <Crown className="w-3 h-3" data-id="element-528" />{' '}
                      {mockUser.vCoins.toLocaleString()} V Coins
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="flex md:flex-col overflow-x-auto md:overflow-visible gap-2 pb-2 md:pb-0 scrollbar-hide" data-id="element-529">
                  {tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <button key={tab.id} onClick={() => setActiveTab(tab.id as Tab)} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all whitespace-nowrap md:whitespace-normal text-left ${isActive ? 'bg-gold/10 text-gold border border-gold/20 shadow-[inset_0_0_15px_rgba(212,175,55,0.1)]' : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'}`} data-id="element-530">
                        <Icon className={`w-5 h-5 ${isActive ? 'text-gold' : 'text-gray-500'}`} data-id="element-531" />
                        <span className="font-medium text-sm" data-id="element-532">{tab.label}</span>
                        {isActive && <ChevronRight className="w-4 h-4 ml-auto hidden md:block" data-id="element-533" />}
                      </button>;
                })}

                  <button onClick={() => logout()} className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-red-400 hover:bg-red-500/10 hover:text-red-300 border border-transparent mt-auto md:mt-4 whitespace-nowrap md:whitespace-normal text-left" data-id="element-534">
                    <LogOut className="w-5 h-5" data-id="element-535" />
                    <span className="font-medium text-sm" data-id="element-536">
                      {t.sidebar.logout}
                    </span>
                  </button>
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-grow min-w-0" data-id="element-537">
              <AnimatePresence mode="wait" data-id="element-538">
                <motion.div key={activeTab} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="h-full" data-id="element-539">
                  {/* OVERVIEW TAB */}
                  {activeTab === 'overview' && <div className="space-y-6" data-id="element-540">
                      <h2 className="text-2xl font-bold font-cinzel mb-6" data-id="element-541">
                        {t.overview.title}
                      </h2>

                      {/* Top Stats Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-542">
                        <div className="glass-panel rounded-xl p-6 border-gold/20 relative overflow-hidden" data-id="element-543">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-[40px] -mr-10 -mt-10" data-id="element-544" />
                          <p className="text-gray-400 text-sm mb-1" data-id="element-545">
                            {t.overview.balance}
                          </p>
                          <h3 className="text-4xl font-bold text-gradient-gold font-cinzel mb-2" data-id="element-546">
                            {mockUser.vCoins.toLocaleString()}
                          </h3>
                          <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-547">
                            Vanguard Coins
                          </p>
                        </div>

                        <div className="glass-panel rounded-xl p-6 border-glowBlue/20" data-id="element-548">
                          <p className="text-gray-400 text-sm mb-1" data-id="element-549">
                            {t.overview.status}
                          </p>
                          <div className="flex items-center gap-3 mb-2" data-id="element-550">
                            {mockUser.isVip ? <>
                                <Crown className="w-8 h-8 text-gold" data-id="element-551" />
                                <h3 className="text-2xl font-bold text-white" data-id="element-552">
                                  {t.overview.vip}
                                </h3>
                              </> : <>
                                <User className="w-8 h-8 text-gray-400" data-id="element-553" />
                                <h3 className="text-2xl font-bold text-white" data-id="element-554">
                                  {t.overview.standard}
                                </h3>
                              </>}
                          </div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-555">
                            {mockUser.email}
                          </p>
                        </div>
                      </div>

                      {/* Characters */}
                      <div className="glass-panel rounded-xl p-6" data-id="element-556">
                        <h3 className="text-xl font-bold font-cinzel mb-4 flex items-center gap-2" data-id="element-557">
                          <Sword className="w-5 h-5 text-gold" data-id="element-558" />{' '}
                          {t.overview.characters}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-559">
                          {mockUser.characters.map(char => <div key={char.id} className="bg-darkerBlue/50 border border-white/5 rounded-lg p-4 hover:border-gold/30 transition-colors" data-id="element-560">
                              <div className="flex justify-between items-start mb-2" data-id="element-561">
                                <h4 className="font-bold text-lg text-white" data-id="element-562">
                                  {char.name}
                                </h4>
                                <span className="text-xs px-2 py-1 bg-white/10 rounded text-gray-300" data-id="element-563">
                                  {char.server}
                                </span>
                              </div>
                              <p className="text-sm text-gold mb-1" data-id="element-564">
                                {char.class}
                              </p>
                              <div className="flex gap-4 text-sm text-gray-400 mt-3" data-id="element-565">
                                <span data-id="element-566">
                                  {t.overview.level}:{' '}
                                  <strong className="text-white" data-id="element-567">
                                    {char.level}
                                  </strong>
                                </span>
                                <span data-id="element-568">
                                  Resets:{' '}
                                  <strong className="text-white" data-id="element-569">
                                    {char.resets}
                                  </strong>
                                </span>
                              </div>
                            </div>)}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="glass-panel rounded-xl p-6" data-id="element-570">
                        <h3 className="text-xl font-bold font-cinzel mb-4 flex items-center gap-2" data-id="element-571">
                          <Shield className="w-5 h-5 text-glowBlue" data-id="element-572" />{' '}
                          {t.overview.stats}
                        </h3>
                        <div className="grid grid-cols-3 gap-4 text-center" data-id="element-573">
                          <div className="bg-darkerBlue/50 rounded-lg p-4" data-id="element-574">
                            <p className="text-gray-400 text-xs uppercase mb-1" data-id="element-575">
                              {t.overview.playTime}
                            </p>
                            <p className="font-bold text-lg text-white" data-id="element-576">
                              {mockUser.playTime}
                            </p>
                          </div>
                          <div className="bg-darkerBlue/50 rounded-lg p-4" data-id="element-577">
                            <p className="text-gray-400 text-xs uppercase mb-1" data-id="element-578">
                              {t.overview.kills}
                            </p>
                            <p className="font-bold text-lg text-green-400" data-id="element-579">
                              {mockUser.kills.toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-darkerBlue/50 rounded-lg p-4" data-id="element-580">
                            <p className="text-gray-400 text-xs uppercase mb-1" data-id="element-581">
                              {t.overview.deaths}
                            </p>
                            <p className="font-bold text-lg text-red-400" data-id="element-582">
                              {mockUser.deaths.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>}

                  {/* BUY V COINS TAB */}
                  {activeTab === 'buy' && <div className="space-y-6" data-id="element-583">
                      <div data-id="element-584">
                        <h2 className="text-2xl font-bold font-cinzel mb-2" data-id="element-585">
                          {t.buy.title}
                        </h2>
                        <p className="text-gray-400" data-id="element-586">{t.buy.subtitle}</p>
                      </div>

                      <div className="glass-panel rounded-xl p-6" data-id="element-587">
                        <h3 className="text-lg font-bold mb-4" data-id="element-588">
                          {t.buy.packages}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-589">
                          {vCoinPackages.map(pkg => {
                        const isSelected = selectedPackage === pkg.id;
                        return <div key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`relative rounded-xl p-5 cursor-pointer transition-all duration-300 border-2 ${isSelected ? 'bg-gold/5 border-gold shadow-[0_0_20px_rgba(212,175,55,0.15)]' : 'bg-darkerBlue border-white/5 hover:border-gold/30'}`} data-id="element-590">
                                {isSelected && <div className="absolute top-3 right-3 w-6 h-6 bg-gold rounded-full flex items-center justify-center" data-id="element-591">
                                    <Check className="w-4 h-4 text-darkerBlue" data-id="element-592" />
                                  </div>}
                                <div className="flex items-center gap-3 mb-2" data-id="element-593">
                                  <Crown className={`w-6 h-6 ${isSelected ? 'text-gold' : 'text-gray-400'}`} data-id="element-594" />
                                  <h4 className={`text-2xl font-bold font-cinzel ${isSelected ? 'text-white' : 'text-gray-300'}`} data-id="element-595">
                                    {pkg.amount.toLocaleString()}
                                  </h4>
                                </div>
                                {pkg.bonus > 0 && <p className="text-sm text-green-400 font-semibold mb-3" data-id="element-596">
                                    + {pkg.bonus} Bonus V Coins
                                  </p>}
                                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center" data-id="element-597">
                                  <span className="text-gray-400 text-sm" data-id="element-598">
                                    Precio
                                  </span>
                                  <span className="text-xl font-bold text-white" data-id="element-599">
                                    ${pkg.price} USD
                                  </span>
                                </div>
                              </div>;
                      })}
                        </div>

                        <button onClick={handleBuy} disabled={buyStatus === 'processing'} className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2" data-id="element-600">
                          {buyStatus === 'processing' ? t.buy.processing : t.buy.purchase}
                        </button>

                        <AnimatePresence data-id="element-601">
                          {buyStatus === 'success' && <motion.div initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0
                      }} className="mt-4 p-4 rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 text-center flex items-center justify-center gap-2" data-id="element-602">
                              <Check className="w-5 h-5" data-id="element-603" /> {t.buy.success}
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>}

                  {/* WITHDRAW TAB (Mimicking the reference image) */}
                  {activeTab === 'withdraw' && <div className="space-y-6" data-id="element-604">
                      <h2 className="text-2xl font-bold font-cinzel mb-6" data-id="element-605">
                        {t.withdraw.title}
                      </h2>

                      <div className="glass-panel rounded-2xl p-6 md:p-10 border-gold/20 relative overflow-hidden max-w-2xl mx-auto" data-id="element-606">
                        {/* Subtle background glow */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-[80px] -mr-32 -mt-32 pointer-events-none" data-id="element-607" />

                        <div className="text-center mb-8 relative z-10" data-id="element-608">
                          <h3 className="text-2xl font-bold font-cinzel text-white mb-2" data-id="element-609">
                            {t.withdraw.title}
                          </h3>
                          <p className="text-gray-400 text-sm" data-id="element-610">
                            {t.withdraw.balance}:{' '}
                            <span className="text-gold font-bold" data-id="element-611">
                              {mockUser.vCoins.toLocaleString()}
                            </span>
                          </p>
                        </div>

                        <div className="space-y-6 relative z-10" data-id="element-612">
                          {/* Amount Input */}
                          <div className="bg-[#1a1510] border border-[#3a2a18] rounded-xl p-4 flex justify-between items-center shadow-inner" data-id="element-613">
                            <div className="flex-grow" data-id="element-614">
                              <label className="block text-xs text-gray-400 mb-1" data-id="element-615">
                                {t.withdraw.amount}
                              </label>
                              <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="0" className="w-full bg-transparent text-2xl font-bold text-white focus:outline-none" data-id="element-616" />
                            </div>
                            <span className="text-gold font-bold text-sm ml-4" data-id="element-617">
                              V COIN
                            </span>
                          </div>

                          {/* Arrow Down */}
                          <div className="flex justify-center" data-id="element-618">
                            <ArrowDown className="w-6 h-6 text-gold/70" data-id="element-619" />
                          </div>

                          {/* Receive Amount */}
                          <div className="bg-[#1a1510] border border-[#3a2a18] rounded-xl p-4 flex justify-between items-center shadow-inner" data-id="element-620">
                            <div data-id="element-621">
                              <label className="block text-xs text-gray-400 mb-1" data-id="element-622">
                                {t.withdraw.receive}
                              </label>
                              <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center" data-id="element-623">
                                <Wallet className="w-5 h-5 text-gold" data-id="element-624" />
                              </div>
                            </div>
                            <div className="text-right" data-id="element-625">
                              <span className="text-2xl font-bold text-white" data-id="element-626">
                                {usdtReceive}
                              </span>
                              <span className="text-gray-400 font-bold text-sm ml-2" data-id="element-627">
                                USDT
                              </span>
                            </div>
                          </div>

                          {/* Confirmation Text */}
                          <div className="text-center py-4" data-id="element-628">
                            <p className="text-gray-300 text-sm" data-id="element-629">
                              {withdrawAmount ? t.withdraw.confirmText.replace('{vcoin}', withdrawAmount).replace('{usdt}', usdtReceive) : 'Ingrese una cantidad para continuar'}
                            </p>
                          </div>

                          {/* Submit Button */}
                          <button onClick={handleWithdraw} disabled={!withdrawAmount || withdrawStatus === 'processing'} className="w-full relative overflow-hidden px-8 py-4 font-cinzel font-bold text-white bg-gradient-to-b from-[#8a6a2a] to-[#4a3a1a] border border-[#a88a4a] rounded-lg transition-all duration-300 hover:brightness-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_15px_rgba(0,0,0,0.5)]" data-id="element-630">
                            {withdrawStatus === 'processing' ? t.withdraw.processing : t.withdraw.button}
                          </button>

                          {/* Status Messages */}
                          <AnimatePresence data-id="element-631">
                            {withdrawStatus === 'success' && <motion.div initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0
                        }} className="p-3 rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 text-sm text-center" data-id="element-632">
                                {t.withdraw.success}
                              </motion.div>}
                            {withdrawStatus === 'error' && <motion.div initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0
                        }} className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm text-center" data-id="element-633">
                                {t.withdraw.error}
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>}

                  {/* UNSTUCK TAB */}
                  {activeTab === 'unstuck' && <div className="space-y-6" data-id="element-634">
                      <div data-id="element-635">
                        <h2 className="text-2xl font-bold font-cinzel mb-2" data-id="element-636">
                          {t.unstuck.title}
                        </h2>
                        <p className="text-gray-400" data-id="element-637">{t.unstuck.subtitle}</p>
                      </div>

                      <div className="glass-panel rounded-xl p-6 max-w-2xl" data-id="element-638">
                        <div className="space-y-6" data-id="element-639">
                          <div data-id="element-640">
                            <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-641">
                              {t.unstuck.selectChar}
                            </label>
                            <select value={selectedChar} onChange={e => setSelectedChar(Number(e.target.value))} className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold/50" data-id="element-642">
                              {mockUser.characters.map(char => <option key={char.id} value={char.id} data-id="element-643">
                                  {char.name} - Nivel {char.level}
                                </option>)}
                            </select>
                          </div>

                          <div className="bg-darkerBlue/50 border border-white/5 rounded-lg p-4 flex items-start gap-4" data-id="element-644">
                            <div className="w-10 h-10 rounded-full bg-glowBlue/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-645">
                              <AlertTriangle className="w-5 h-5 text-glowBlue" data-id="element-646" />
                            </div>
                            <div data-id="element-647">
                              <p className="text-sm text-gray-400 mb-1" data-id="element-648">
                                {t.unstuck.location}
                              </p>
                              <p className="font-bold text-white mb-2" data-id="element-649">
                                {mockUser.characters.find(c => c.id === selectedChar)?.location}
                              </p>
                              <p className="text-sm text-gray-300" data-id="element-650">
                                {t.unstuck.warning}
                              </p>
                              <p className="text-xs text-gold mt-2" data-id="element-651">
                                {t.unstuck.cooldown}
                              </p>
                            </div>
                          </div>

                          <button onClick={handleUnstuck} disabled={unstuckStatus === 'processing'} className="w-full bg-transparent border-2 border-glowBlue text-glowBlue hover:bg-glowBlue hover:text-white font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2" data-id="element-652">
                            <Wrench className="w-5 h-5" data-id="element-653" />
                            {unstuckStatus === 'processing' ? t.unstuck.processing : t.unstuck.button}
                          </button>

                          <AnimatePresence data-id="element-654">
                            {unstuckStatus === 'success' && <motion.div initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0
                        }} className="p-3 rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 text-sm text-center" data-id="element-655">
                                {t.unstuck.success}
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>}

                  {/* SETTINGS TAB */}
                  {activeTab === 'settings' && <div className="space-y-6" data-id="element-656">
                      <h2 className="text-2xl font-bold font-cinzel mb-6" data-id="element-657">
                        {t.settings.title}
                      </h2>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-658">
                        {/* Account Info */}
                        <div className="glass-panel rounded-xl p-6 h-fit" data-id="element-659">
                          <h3 className="text-lg font-bold mb-4 border-b border-white/10 pb-2" data-id="element-660">
                            {t.settings.info}
                          </h3>
                          <div className="space-y-4" data-id="element-661">
                            <div data-id="element-662">
                              <p className="text-sm text-gray-400 mb-1" data-id="element-663">
                                Usuario
                              </p>
                              <p className="font-medium text-white bg-darkerBlue/50 px-3 py-2 rounded border border-white/5" data-id="element-664">
                                {mockUser.username}
                              </p>
                            </div>
                            <div data-id="element-665">
                              <p className="text-sm text-gray-400 mb-1" data-id="element-666">
                                {t.settings.email}
                              </p>
                              <p className="font-medium text-white bg-darkerBlue/50 px-3 py-2 rounded border border-white/5" data-id="element-667">
                                {mockUser.email}
                              </p>
                            </div>
                            <div data-id="element-668">
                              <p className="text-sm text-gray-400 mb-1" data-id="element-669">
                                {t.settings.regDate}
                              </p>
                              <p className="font-medium text-white bg-darkerBlue/50 px-3 py-2 rounded border border-white/5" data-id="element-670">
                                {mockUser.regDate}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Change Password */}
                        <div className="glass-panel rounded-xl p-6" data-id="element-671">
                          <h3 className="text-lg font-bold mb-4 border-b border-white/10 pb-2" data-id="element-672">
                            {t.settings.changePass}
                          </h3>
                          <form onSubmit={handleSettings} className="space-y-4" data-id="element-673">
                            <div data-id="element-674">
                              <label className="block text-sm text-gray-400 mb-1" data-id="element-675">
                                {t.settings.currentPass}
                              </label>
                              <input type="password" required className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold/50" data-id="element-676" />
                            </div>
                            <div data-id="element-677">
                              <label className="block text-sm text-gray-400 mb-1" data-id="element-678">
                                {t.settings.newPass}
                              </label>
                              <input type="password" required className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold/50" data-id="element-679" />
                            </div>
                            <div data-id="element-680">
                              <label className="block text-sm text-gray-400 mb-1" data-id="element-681">
                                {t.settings.confirmPass}
                              </label>
                              <input type="password" required className="w-full bg-darkerBlue border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold/50" data-id="element-682" />
                            </div>
                            <button type="submit" className="w-full btn-secondary py-2 mt-2" data-id="element-683">
                              {t.settings.updateBtn}
                            </button>
                          </form>

                          <AnimatePresence data-id="element-684">
                            {settingsStatus === 'success' && <motion.div initial={{
                          opacity: 0,
                          y: 10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0
                        }} className="mt-4 p-3 rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 text-sm text-center" data-id="element-685">
                                {t.settings.success}
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-686" />
    </div>;
}